export interface BouncerResult {
  isValid: boolean;
  reason?: string;
}

// Main validation function
export function validateText(text: string): BouncerResult {
  let result: BouncerResult = isValidText(text);
  if (!result.isValid) {
    console.log(`Bouncer rejected text for reason "${result.reason}":`, text);
  }
  return result;
}

// Validation function's logic
function isValidText(text: string): BouncerResult {
  if (hasConsecutiveRepeatedSubstring(text)) {
    return { isValid: false, reason: 'consecutive_repetition' };
  }
  if (isSentence(text, 'thank you for watching')) {
    return { isValid: false, reason: 'banned_sentence_1' };
  }
  // More filters can be added here
  return { isValid: true };
}

// Consecutive repetition filter (10+ repeats of 1+ char substring)
export function hasConsecutiveRepeatedSubstring(str: string, minRepeats = 10): boolean {
  const regex = new RegExp(`(.+?)\\1{${minRepeats - 1},}`);
  return regex.test(str);
}

// Normalize text for comparison: convert fullwidth to ASCII, lowercase,
// and keep only alphanumeric, kanji, hiragana, and katakana characters
function normalize(str: string): string {
  return str
    .normalize("NFKC") // fullwidth → ASCII, compatibility fold
    .toLowerCase()
    .replace(
      /[^a-z0-9\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff]/gi,
      ""
    );
}

// Check is a sentence matches another one once all non: alpha-num,
// kanjis, hiraganas, katakanas and fullwidth latin characters
// are removed
export function isSentence(input: string, reference: string): boolean {
  return normalize(input) === normalize(reference);
}

// --- Bouncer helpers for useAppStore ---

type TranslatorFn = (lang: string) => (key: string) => string;
let translator: TranslatorFn | null = null;

// Initialize the bouncer with a translator function for localized error messages
export function initBouncer(t: TranslatorFn) {
  translator = t;
}

// Get a localized bouncer error message for the given language
function getErrorMessage(lang: string): string {
  if (!translator) return "[Translation error]";
  return translator(lang)("[bouncer_error]");
}

// Get the first target language or fallback to preferred/default
function getFirstLang(targetLanguages: string[], preferredLanguage: string): string {
  return targetLanguages[0] || preferredLanguage || 'en';
}

// Build an empty translations record for all target languages
function buildEmptyTranslations(targetLanguages: string[]): Record<string, string> {
  const empty: Record<string, string> = {};
  for (const lang of targetLanguages) {
    empty[lang] = '';
  }
  return empty;
}

// Validate an incoming message and all its translations.
// Returns sanitized data with bouncerRejected=true if any text fails validation.
export function validateIncomingMessage(
  message: string,
  translations: Record<string, string> | undefined,
  targetLanguages: string[],
  preferredLanguage: string
): { message: string; translations: Record<string, string>; bouncerRejected: boolean } {
  let anyInvalid = !validateText(message).isValid;
  if (translations && !anyInvalid) {
    for (const text of Object.values(translations)) {
      if (!validateText(text).isValid) {
        anyInvalid = true;
        break;
      }
    }
  }
  if (anyInvalid) {
    const firstLang = getFirstLang(targetLanguages, preferredLanguage);
    return {
      message: getErrorMessage(firstLang),
      translations: buildEmptyTranslations(targetLanguages),
      bouncerRejected: true,
    };
  }
  return { message, translations: translations || {}, bouncerRejected: false };
}

// Validate a single translation text.
// Returns rejection data if invalid, null if valid.
export function validateTranslation(
  fullText: string,
  targetLanguages: string[],
  preferredLanguage: string
): { message: string; translations: Record<string, string>; bouncerRejected: true } | null {
  if (!validateText(fullText).isValid) {
    const firstLang = getFirstLang(targetLanguages, preferredLanguage);
    return {
      message: getErrorMessage(firstLang),
      translations: buildEmptyTranslations(targetLanguages),
      bouncerRejected: true,
    };
  }
  return null;
}

// Get pre-built rejection data with error message and empty translations
export function getRejectionData(
  targetLanguages: string[],
  preferredLanguage: string
): { message: string; translations: Record<string, string> } {
  const firstLang = getFirstLang(targetLanguages, preferredLanguage);
  return {
    message: getErrorMessage(firstLang),
    translations: buildEmptyTranslations(targetLanguages),
  };
}
